import {
  Intercom,
  show as intercomShow,
  shutdown as intercomShutdown,
  update as intercomUpdate
} from '@intercom/messenger-js-sdk';
import { posthog, registerAnalyticsContext } from '@kiku/analytics/client';
import type { AuthenticatedSession } from '@kiku/auth/types';
import { frontendConfig } from '@kiku/config/env/frontend';
import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  useParams
} from '@tanstack/react-router';
import {
  BarChart2,
  BriefcaseBusiness,
  Building2,
  CircleHelp,
  CircleUserRound,
  FileText,
  GitBranch,
  Inbox,
  Languages,
  LogOut,
  Menu,
  MessageSquare,
  MoreVertical,
  Puzzle,
  Settings,
  Sparkles,
  Users
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { useSentryUser } from '~/hooks/use-sentry-user';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';
import { cn } from '~/lib/utils';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.fetchQuery(meQueryOptions);
      await context.queryClient.prefetchQuery(departmentsQueryOptions);
    } catch {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { error: 'session_expired' } });
    }
  },
  component: AuthenticatedLayout
});

function NavLink({
  to,
  params,
  icon: Icon,
  label,
  active
}: {
  to: string;
  params?: Record<string, string>;
  icon: React.ElementType;
  label: string;
  active: boolean;
}): React.ReactNode {
  return (
    <Link
      to={to}
      params={params}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors',
        active
          ? 'bg-sidebar-primary font-medium text-sidebar-primary-foreground'
          : 'text-sidebar-foreground hover:bg-sidebar-accent'
      )}
    >
      <Icon className="size-4 shrink-0" />
      {label}
    </Link>
  );
}

function NavButton({
  icon: Icon,
  label,
  onClick
}: {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
}): React.ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
    >
      <Icon className="size-4 shrink-0" />
      {label}
    </button>
  );
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' }
];

function SidebarContent({
  pathname,
  session
}: {
  pathname: string;
  session: AuthenticatedSession | undefined;
}): React.ReactNode {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const activeDeptSlug = 'deptSlug' in params ? params.deptSlug : undefined;
  const { data: departmentsData } = useQuery(departmentsQueryOptions);
  const activeDept = (departmentsData?.departments ?? []).find((d) => d.slug === activeDeptSlug);
  const controlCenterItems = [
    { icon: Building2, label: t('nav.company') },
    { icon: Sparkles, label: t('nav.suggestedCandidates') },
    { icon: Inbox, label: t('nav.unsolicitedApplications') },
    { icon: FileText, label: t('nav.templates') }
  ];

  return (
    <>
      <div className="flex h-14 items-center px-4 my-6">
        <img src="/kiku_logo.png" alt="Kiku" className="h-7 w-auto" />
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 px-2">
        <p className="px-3 pb-1 pt-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('nav.workspace')}
        </p>
        <NavLink
          to="/jobs"
          icon={BriefcaseBusiness}
          label={t('nav.jobs')}
          active={pathname.includes('/jobs')}
        />
        <NavButton icon={BarChart2} label={t('nav.analytics')} />

        <p className="px-3 pb-1 pt-5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('nav.controlCenter')}
        </p>
        {session?.role !== 'user' && session?.orgSlug && (
          <NavLink
            to="/org/$orgSlug/team"
            params={{ orgSlug: session.orgSlug }}
            icon={Users}
            label={t('nav.team')}
            active={pathname.endsWith('/team')}
          />
        )}
        {(session?.role === 'admin' || session?.role === 'org_manager') && session.orgSlug && (
          <NavLink
            to="/org/$orgSlug/integrations"
            params={{ orgSlug: session.orgSlug }}
            icon={Puzzle}
            label={t('nav.integrations')}
            active={pathname.includes('/integrations')}
          />
        )}
        {(session?.role === 'admin' || session?.role === 'org_manager') && session.orgSlug && (
          <NavLink
            to="/org/$orgSlug/workflow"
            params={{ orgSlug: session.orgSlug }}
            icon={GitBranch}
            label={t('nav.workflow')}
            active={pathname.includes('/workflow')}
          />
        )}
        {(session?.role === 'admin' ||
          session?.role === 'org_manager' ||
          session?.role === 'dept_manager') &&
          activeDept && (
            <NavLink
              to="/org/$orgSlug/department/$deptSlug/communications"
              params={{ orgSlug: session.orgSlug, deptSlug: activeDept.slug }}
              icon={MessageSquare}
              label={t('nav.agentCommunications')}
              active={pathname.includes('/communications')}
            />
          )}
        {controlCenterItems.map(({ icon, label }) => (
          <NavButton key={label} icon={icon} label={label} />
        ))}

        {session?.role === 'admin' && (
          <>
            <p className="px-3 pb-1 pt-5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('nav.administration')}
            </p>
            <NavLink
              to="/admin/organizations"
              icon={Building2}
              label={t('nav.adminOrganizations')}
              active={pathname.startsWith('/admin')}
            />
          </>
        )}
      </nav>

      <div className="flex flex-col gap-0.5 px-2 pb-3">
        <NavButton icon={CircleHelp} label={t('nav.help')} onClick={intercomShow} />
        <DropdownMenu>
          <DropdownMenuTrigger className="mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left hover:bg-sidebar-accent">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
              <CircleUserRound className="size-5 text-muted-foreground" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-medium text-foreground">{session?.name}</span>
              <span className="truncate text-xs text-muted-foreground">{session?.email}</span>
            </div>
            <MoreVertical className="size-4 shrink-0 text-muted-foreground" />
          </DropdownMenuTrigger>

          <DropdownMenuContent side="top" align="start" className="w-64">
            {/* User info header */}
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted">
                <CircleUserRound className="size-5 text-muted-foreground" />
              </div>
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium text-foreground">
                  {session?.name}
                </span>
                <span className="truncate text-xs text-muted-foreground">{session?.email}</span>
              </div>
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={() => void navigate({ to: '/settings' })}>
              <Settings className="size-4" />
              {t('nav.settings')}
            </DropdownMenuItem>

            {/* Language submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Languages className="size-4" />
                {t('nav.language')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={i18n.language}
                  onValueChange={(lang: string) => void i18n.changeLanguage(lang)}
                >
                  {LANGUAGES.map(({ code, label }) => (
                    <DropdownMenuRadioItem key={code} value={code}>
                      {label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuItem>
              <CircleHelp className="size-4" />
              {t('nav.help')}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              variant="destructive"
              onClick={() => {
                window.location.href = '/auth/logout';
              }}
            >
              <LogOut className="size-4" />
              {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}

function AuthenticatedLayout(): React.ReactNode {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { data: session } = useQuery(meQueryOptions);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    posthog.capture('$pageview', { $current_url: window.location.href });
  }, [pathname]);

  useEffect(() => {
    if (!session) return;

    posthog.identify(session.userId, {
      email: session.email,
      name: session.name,
      role: session.role,
      org_id: session.orgId,
      org_slug: session.orgSlug,
      tenant_id: session.tenantId
    });
    posthog.group('company', session.orgId, {
      org_slug: session.orgSlug,
      tenant_id: session.tenantId
    });
    registerAnalyticsContext({
      userId: session.userId,
      orgId: session.orgId,
      tenantId: session.tenantId
    });

    const appId = frontendConfig.VITE_PUBLIC_INTERCOM_APP_ID;
    if (appId) {
      Intercom({
        app_id: appId,
        user_id: session.userId,
        email: session.email,
        name: session.name,
        hide_default_launcher: window.innerWidth < 640,
        alignment: 'left',
        horizontal_padding: 195
      });
    }

    return () => {
      if (appId) intercomShutdown();
    };
  }, [session]);

  useEffect(() => {
    if (!frontendConfig.VITE_PUBLIC_INTERCOM_APP_ID) return;
    const mq = window.matchMedia('(max-width: 639px)');
    const update = (e: MediaQueryListEvent | MediaQueryList): void => {
      intercomUpdate({ hide_default_launcher: e.matches });
    };
    update(mq);
    mq.addEventListener('change', update);
    return () => {
      mq.removeEventListener('change', update);
    };
  }, []);

  useSentryUser(session);

  const sidebarProps = { pathname, session };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background sm:flex-row">
      {/* Mobile header */}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-sidebar px-4 sm:hidden">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => {
            setMobileOpen(true);
          }}
          aria-label={t('nav.openMenu')}
        >
          <Menu className="size-5" />
        </Button>
        <img src="/kiku_logo.png" alt="Kiku" className="h-6 w-auto" />
      </header>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-60 p-0 pl-2">
          <SheetTitle className="sr-only">{t('nav.openMenu')}</SheetTitle>
          <SidebarContent {...sidebarProps} />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside className="hidden w-60 flex-shrink-0 flex-col overflow-y-auto pl-2 sm:flex">
        <SidebarContent {...sidebarProps} />
      </aside>

      <div className="flex-1 min-h-0 min-w-0 p-2 sm:p-3">
        <div className="h-full overflow-hidden rounded-xl bg-card shadow-sm">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
